/**
 * Function that receives a kg and converts to lbs
 */
export function kilogramsToPounds(kg: number): number {
  return kg * 2.20462;
}

/**
 * Convert the value from range 0..1 into percentage 0..100 and optionally round or truncate the decimal places.
 */
export function percent(
  value: number,
  opts?: { round?: number; truncate?: number }
): number {
  let result = value * 100;
  if (opts?.round != null) result = round(result, opts.round);
  if (opts?.truncate != null) result = truncate(result, opts.truncate);
  return result;
}

/**
 * Opposite of `percent`, converts a value of 0..100 into a value of 0..1.
 */
export function fraction(value: number): number {
  return value / 100;
}

/**
 * Rounds number. If `precision` is a positive number, it rounds up to
 * `precision` decimal places. If `precision` is a negative number, it rounds up
 * to `precision` whole places. `precision` defaults to 0.
 *
 * Example:
 * ```
 *   round(1234.5678, 2) // 1234.57
 *   round(1234.5678, -2) // 1200
 *   round(1234.5678) // 1235
 * ```
 */
export function round(value: number, precision = 0): number {
  const factor = 10 ** precision;

  if (precision > 0) {
    return Math.round(value * factor) / factor;
  }

  if (precision < 0) {
    return Math.round(value / factor) * factor;
  }

  return Math.round(value);
}

/**
 * Returns ordinal of number.
 *
 * Example:
 * ```
 *   ordinal(1) // st
 *   ordinal(123) // rd
 * ```
 */
export function ordinal(value: number): string {
  let ord = "th";

  if (value % 10 == 1 && value % 100 != 11) {
    ord = "st";
  } else if (value % 10 == 2 && value % 100 != 12) {
    ord = "nd";
  } else if (value % 10 == 3 && value % 100 != 13) {
    ord = "rd";
  }

  return ord;
}

/**
 * Returns number with ordinal.
 *
 * Example:
 * ```
 *   withOrdinal(1) // 1st
 *   withOrdinal(123) // 123rd
 * ```
 */
export function withOrdinal(value: number): string {
  return `${value}${ordinal(value)}`;
}

/**
 * Truncates number. If `precision` is a positive number, it truncates up to
 * `precision` decimal places. If `precision` is a negative number, it truncates
 * up to `precision` whole places. `precision` defaults to 0.
 *
 * Example:
 * ```
 *   truncate(1234.5678, 2) // 1234.56
 *   truncate(1234.5678, -2) // 1200
 *   truncate(1234.5678) // 1234
 * ```
 */
export function truncate(value: number, precision = 0): number {
  const factor = 10 ** precision;

  if (precision > 0) {
    return Math.trunc(value * factor) / factor;
  }

  if (precision < 0) {
    return Math.trunc(value / factor) * factor;
  }

  return Math.trunc(value);
}
